/**
 * Колонка «Срок доставки» — подгруппа категории «Кластеры», на каждый кластер:
 *
 *   общ. 5,8 д · лок. 1,9 д · не лок. 7,4 д
 *
 * Про заказы покупателей ИЗ этого кластера (cluster_to): сколько в среднем
 * дней прошло от создания заказа до доставки — все / отгруженные складом
 * того же кластера (локальные, cluster_from = cluster_to) / привезённые из
 * других кластеров.
 *
 * Откуда дата доставки. Отправления Ozon (FBO) не несут отметки времени
 * доставки — только created_at и in_process_at. Берём день, когда Ozon
 * засчитал продажу: строка `sale` в finance_accrual_lines
 * (lib/finance-accruals) с номером отправления. Это конец пути посылки с
 * точностью до дня (проверено на живых данных: Москва локально ≈3 дня,
 * привозные ≈5); у пунктов выдачи начисление может опережать момент, когда
 * покупатель забрал заказ. Срок считается в целых днях между московскими
 * датами создания и начисления; в среднем по десяткам заказов цифра честная.
 *
 * Окно — доставки за последние 30 дней (по дате начисления); заказы могли
 * быть созданы раньше окна. Данные лежат в готовом виде (сумма дней и число
 * заказов), чтобы у строки модели срок считался по всем размерам вместе, а не
 * средним из средних.
 */

export const CLUSTER_DELIVERY_DAYS_METRIC = "cluster_delivery_days";
export const CLUSTER_DELIVERY_DAYS_COLUMN_WIDTH = 196;
export const DELIVERY_DAYS_WINDOW = 30;

export type DeliveryDaysStat = {
  /** Доставленных заказов и сумма дней «создан → доставлен» по ним. */
  n: number;
  sum: number;
  /** Из них отгруженных складом того же кластера. */
  localN: number;
  localSum: number;
};

/** Кластер покупателя → накопленные дни и заказы. */
export type DeliveryDaysByCluster = Record<string, DeliveryDaysStat>;

export type DeliveryDaysSummary = {
  all: number | null;
  local: number | null;
  nonLocal: number | null;
  orders: number;
  localOrders: number;
};

export function emptyDeliveryDaysStat(): DeliveryDaysStat {
  return { n: 0, sum: 0, localN: 0, localSum: 0 };
}

export function addDeliveryDays(target: DeliveryDaysStat, source: DeliveryDaysStat | undefined): DeliveryDaysStat {
  if (!source) return target;
  target.n += source.n;
  target.sum += source.sum;
  target.localN += source.localN;
  target.localSum += source.localSum;
  return target;
}

/** Срок по кластеру для набора товаров (у модели — все размеры вместе). */
export function deliveryDaysForCluster(list: ReadonlyArray<DeliveryDaysByCluster | null | undefined>, cluster: string): DeliveryDaysSummary {
  const total = emptyDeliveryDaysStat();
  for (const byCluster of list) addDeliveryDays(total, byCluster?.[cluster]);
  return summarizeDeliveryDays(total);
}

export function summarizeDeliveryDays(stat: DeliveryDaysStat): DeliveryDaysSummary {
  const nonLocalN = stat.n - stat.localN;
  const nonLocalSum = stat.sum - stat.localSum;
  return {
    all: stat.n > 0 ? stat.sum / stat.n : null,
    local: stat.localN > 0 ? stat.localSum / stat.localN : null,
    nonLocal: nonLocalN > 0 ? nonLocalSum / nonLocalN : null,
    orders: stat.n,
    localOrders: stat.localN,
  };
}

export function formatDeliveryDays(value: number | null): string {
  if (value === null) return "—";
  return `${value.toLocaleString("ru-RU", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} д`;
}

/** Текст ячейки: «общ. 5,8 д · лок. 1,9 д · не лок. 7,4 д»; без заказов — пусто. */
export function deliveryDaysText(summary: DeliveryDaysSummary): string {
  if (summary.all === null) return "";
  return `общ. ${formatDeliveryDays(summary.all)} · лок. ${formatDeliveryDays(summary.local)} · не лок. ${formatDeliveryDays(summary.nonLocal)}`;
}
